import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { apiService } from '../../services/api';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { normalizeRut } from '../../utils/formatters';
import { DIAS_NUEVO, diasDesdeIngreso, tieneNuevoForzado } from '../../utils/personalNuevo';

interface PersonaNueva {
  rut: string;
  nombre: string;
  cargo?: string;
  detalle?: string; // centro de costo / sede
  diasDesdeIngreso: number; // antigüedad para ordenar (más reciente primero)
  permanente: boolean; // badge fijado a mano en el backend (forzar_nuevo)
}

const formatNombre = (p: any): string =>
  p.nombres ||
  p.display_name ||
  `${p.nombre || ''} ${p.apellido || ''}`.trim() ||
  p.rut;

export const PersonalRecienCreados: React.FC = () => {
  // Candidatos: los que el backend marca con es_nuevo=true. Sobre esa lista se
  // reaplica el corte de DIAS_NUEVO, porque el backend no expira los
  // forzar_nuevo (ver src/utils/personalNuevo.ts).
  const { data, isLoading, isError } = useQuery(
    ['personal-nuevos-badge'],
    async () => {
      try {
        return await apiService.getPersonalNuevos();
      } catch (err) {
        console.error('Error en getPersonalNuevos:', err);
        return [];
      }
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const ingresos = useMemo<PersonaNueva[]>(() => {
    const rows: any[] = Array.isArray(data) ? data : [];
    const porRut = new Map<string, { row: any; dias: number }>();

    for (const p of rows) {
      // No mostrar personal desvinculado en el dashboard.
      const estado = (p?.estado_nombre || p?.estado || '').toString().toLowerCase();
      if (estado.includes('desvinc')) continue;

      // Corte por antigüedad real, sin importar lo que diga es_nuevo. Sin fecha
      // no se puede acreditar la ventana, así que tampoco entra.
      const dias = diasDesdeIngreso(p);
      if (dias === null || dias < 0 || dias > DIAS_NUEVO) continue;

      const key = normalizeRut(p?.rut || p?.id);
      if (!key) continue;
      if (!porRut.has(key)) porRut.set(key, { row: p, dias });
    }

    return Array.from(porRut.values())
      .map(({ row: p, dias }) => {
        const detalle = p.centro_costo || p.sede || p.area || undefined;
        return {
          rut: p.rut || p.id,
          nombre: formatNombre(p),
          cargo: p.cargo || undefined,
          detalle: detalle ? String(detalle) : undefined,
          diasDesdeIngreso: dias,
          permanente: tieneNuevoForzado(p),
        } as PersonaNueva;
      })
      // Más recientes primero: menor antigüedad arriba.
      .sort((a, b) => a.diasDesdeIngreso - b.diasDesdeIngreso);
  }, [data]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 shadow-sm flex-shrink-0">
            <UserPlus className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700">Nuevo personal</h3>
            <p className="text-xs text-gray-400">
              Personal que ingresó en los últimos {DIAS_NUEVO} días
            </p>
          </div>
        </div>
        {!isLoading && !isError && (
          <span className="inline-flex items-center justify-center min-w-[2rem] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold border border-emerald-200">
            {ingresos.length}
          </span>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <LoadingSpinner size="md" />
        </div>
      )}

      {!isLoading && isError && (
        <div className="text-sm text-red-500 py-8 text-center">
          No se pudo cargar el listado de personal nuevo.
        </div>
      )}

      {!isLoading && !isError && ingresos.length === 0 && (
        <div className="text-sm text-gray-400 py-8 text-center">
          No hay personal nuevo en los últimos {DIAS_NUEVO} días.
        </div>
      )}

      {!isLoading && !isError && ingresos.length > 0 && (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {ingresos.map((p) => (
            <button
              key={normalizeRut(p.rut) || p.rut}
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('openPersonalDetailModal', {
                    detail: { rutPersona: p.rut, personalId: p.rut },
                  })
                )
              }
              title={`Ver ficha de ${p.nombre}`}
              className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 hover:border-emerald-300 hover:bg-emerald-50/60 hover:shadow-sm transition-all cursor-pointer"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0">
                {p.nombre.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800 truncate" title={p.nombre}>
                    {p.nombre}
                  </span>
                  <span className="text-[11px] font-mono text-gray-400 flex-shrink-0">{p.rut}</span>
                  <span
                    className="ml-auto text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex-shrink-0"
                    title={`Ingresó hace ${p.diasDesdeIngreso} día${p.diasDesdeIngreso === 1 ? '' : 's'}`}
                  >
                    {p.diasDesdeIngreso === 0 ? 'Hoy' : `Hace ${p.diasDesdeIngreso} d`}
                  </span>
                  {p.permanente && (
                    <span
                      className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex-shrink-0"
                      title="Etiqueta fijada manualmente: no expira"
                    >
                      Fijado
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {p.cargo && <span className="truncate">{p.cargo}</span>}
                  {p.cargo && p.detalle && <span className="text-gray-300">·</span>}
                  {p.detalle && <span className="truncate">{p.detalle}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-gray-100">
        <Link
          to="/personal"
          className="text-sm text-emerald-600 hover:text-emerald-800 hover:underline font-medium"
        >
          Ver todo el personal →
        </Link>
      </div>
    </div>
  );
};
